import {
  useCancellableSubscriptionCard,
  useOpenSubscriptionManagement,
} from 'features/user';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { CancelSubscriptionSurveyDialog } from 'shared/dialogs/cancel-subscription-survey';
import { ID } from 'shared/types/id';

import * as Styled from './subscription-management-links.style';

const BASE = 'forms.user-settings.sections.subscriptions';

export type SubscriptionManagementLinksProps = {
  stripeCustomerId?: ID;
  managementUrl?: string;
  isApple?: boolean;
};

export const SubscriptionManagementLinks: React.FC<
  SubscriptionManagementLinksProps
> = ({ stripeCustomerId, managementUrl: initialManagementUrl, isApple }) => {
  const { t } = useTranslation();
  const { card } = useCancellableSubscriptionCard();
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);
  const [isManageInitiated, setIsManageInitiated] = useState(false);
  const {
    fetch: fetchStripeManagementUrl,
    url: stripeManagementUrl,
    isLoading,
  } = useOpenSubscriptionManagement(stripeCustomerId);

  // initialManagementUrl can be an empty string
  const managementUrl = initialManagementUrl || stripeManagementUrl;
  // Only spin for a manage click the user made here — the survey shares this
  // query and its portal fetch must not spin this button.
  const showManageLoader = isLoading && isManageInitiated;
  const manageLabel = t(
    isApple
      ? `${BASE}.premium.button.manage-in-ios.label`
      : `${BASE}.premium.button.label`,
  );

  const onManageFetch = () => {
    setIsManageInitiated(true);
    fetchStripeManagementUrl();
  };

  const openSurvey = () => setIsSurveyOpen(true);
  const closeSurvey = () => setIsSurveyOpen(false);

  return (
    <>
      <Styled.Container>
        {managementUrl ? (
          <Button
            as="a"
            // @ts-ignore
            href={managementUrl}
            target="_blank"
            size={Sizes.Small}
            variant={Variants.Contained}
            color={Colors.TextPrimary}
          >
            {manageLabel}
          </Button>
        ) : (
          <Button
            onClick={onManageFetch}
            size={Sizes.Small}
            variant={Variants.Contained}
            color={Colors.TextPrimary}
            disabled={showManageLoader}
          >
            {manageLabel}
            {showManageLoader && (
              <Styled.ButtonLoader color="primary" size={12} />
            )}
          </Button>
        )}

        {card && (
          <Button
            onClick={openSurvey}
            size={Sizes.Small}
            variant={Variants.Contained}
            color={Colors.TextPrimary}
          >
            {t(`${BASE}.premium.button.cancel.label`)}
          </Button>
        )}
      </Styled.Container>

      {isSurveyOpen && <CancelSubscriptionSurveyDialog onClose={closeSurvey} />}
    </>
  );
};
